import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { PROJECTS, DA2 } from "@/lib/siteConfig";

const DA = PROJECTS.find((p) => p.id === "dealalert");

// Compact flagship teaser on the homepage — the full case study lives at /references/dealalert.
export const DealAlertTeaser = () => {
  const { t } = useLang();

  return (
    <section
      id="case-dealalert-teaser"
      data-testid="dealalert-teaser"
      className="relative py-20 sm:py-28 bg-[#070a12] border-y border-white/5 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute top-[-10%] left-[-8%] w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(0,212,255,0.08), transparent 70%)" }}
      />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
              {t.dealalert.kicker} — {t.categories[DA.categoryKey]}
            </p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-[-0.025em] leading-[1.1] text-foreground">
              {t.dealalert.headline}
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">{t.dealalert.subline}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {DA.badges.map((b) => (
                <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                  {b}
                </Badge>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                data-testid="dealalert-teaser-case-link"
                to="/references/dealalert"
                className="inline-flex items-center gap-2 rounded-full bg-[#00d4ff] text-[#05070d] hover:bg-[#33ddff] font-medium text-sm px-6 h-11 shadow-[0_0_24px_rgba(0,212,255,0.18)] transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
              >
                {t.caseNav.viewCase} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                data-testid="dealalert-teaser-live-link"
                href={DA.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
              >
                dealalert.ai <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="relative">
              <DeviceFrameDesktop url="dealalert.ai" testId="dealalert-teaser-frame">
                <img
                  src={DA2.hero.src}
                  alt={DA2.hero.alt}
                  width={DA2.hero.w}
                  height={DA2.hero.h}
                  loading="lazy"
                  className="block w-full h-auto"
                />
              </DeviceFrameDesktop>
              <div className="absolute -bottom-9 -right-2 sm:right-7 w-[24%] max-w-[165px]">
                <DeviceFramePhone testId="dealalert-teaser-phone">
                  <img
                    src={DA2.heroPhone.src}
                    alt={DA2.heroPhone.alt}
                    width={DA2.heroPhone.w}
                    height={DA2.heroPhone.h}
                    loading="lazy"
                    className="block w-full h-auto"
                  />
                </DeviceFramePhone>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
